import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import { Typography, Form, Row, Col, Input, Card, Radio, Space, Button, Divider, Tabs, Image, Checkbox, message, Select, Drawer } from "antd";
import InputMask from "react-input-mask";
import moment from "moment";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { getDiseasesActive } from "@/redux/actions/ListDiseasesActions";
import BaseSelect from "@/components/Elements/BaseSelect";
import BaseCheckBoxList from "@/components/Elements/BaseCheckboxes";
import ToothChartGeneral from "@/components/tooth-chart/ToothChartGeneral";
import ToothChartChild from "@/components/tooth-chart/ToothChartChild";
import BaseRadioList from "@/components/Elements/BaseRadios";
import { createGeneralExam, updateGeneralExam, getSpecificGeneralExam, getGeneralExamsByCustomer } from "@/redux/actions/generalExamActions";
import { customerGender, customerTypes, ethnicOptions, formatDateForInputMask, occupationOptions } from "@/utils/helpers";
import { getSpecificCustomer } from "@/redux/actions/customerActions";
import { getListProvinces, getListWards } from "@/redux/actions/RegionsActions";
import { isMobile } from "react-device-detect";

const { Text } = Typography;
const { TabPane } = Tabs;
const { Option, OptGroup } = Select;

const GeneralExamForm = (props) => {

    const { visible, onCancel, customer, record } = props;

    const customerId = customer?.id;

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const query = useMemo(() => {
        return qs.parse(location.search.slice(1));
    }, [location.search]);

    const [form] = Form.useForm();

    const [isApproved, setIsApproved] = useState(false);
    const [toothType, setToothType] = useState(query.tooth_type || "adult");
    const [activeToggle, setActiveToggle] = useState(null); // "yes" | "no" | null
    const [wards, setWards] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [toothDataByType, setToothDataByType] = useState({
        adult: [],
        children: [],
    });

    const doctors = useSelector((state) => state.config.doctors) || [];
    const config = useSelector((state) => state.config);
    const consultants = useSelector((state) => state.config.consultants) || [];
    const diseases = useSelector((state) => state.ListDiseasesReducer.diseases_active_list);
    const provinces = useSelector((state) => state.RegionsReducer.list_provinces);
    const currentExam = useSelector(s => s.generalExamReducer.currentGeneralExam);

    const normalizeDiseaseKey = (name) => name.trim().replace(/\s+/g, " ");

    useEffect(() => {
        if (customer?.province_code) {
            loadWards(customer?.province_code);
        }
    }, [customer?.province_code]);


    useEffect(() => {
        dispatch(getDiseasesActive());
        dispatch(getListProvinces());
    }, [form]);

    // Tạo defaultValues
    const defaultValues = useMemo(() => ({
        customer_name: customer?.full_name,
        customer_code: customer?.code,
        birthday: formatDateForInputMask(customer?.birthday, false),
        age: customer?.age,
        gender: customer?.gender,
        occupation: customer?.occupation,
        ethnicity: customer?.ethnicity,
        nationality: customer?.nationality || "Việt Nam",
        province_code: customer?.province_code,
        wards_code: customer?.wards_code,
        hamlet: customer?.hamlet,
        address: customer?.address,
        customer_type: customer?.customer_type,
        bhyt_number: customer?.bhyt_number,
        identity_number: customer?.identity_number,
        relative_info: customer?.relative_info,
        relative_info_phone: customer?.relative_info_phone,
        note: customer?.note,
        bac_si_phu_trach: null,
        nhan_vien_tu_van_ids: [],
        // đảm bảo luôn có danh sách bệnh lý, nếu DB chưa có thì khởi tạo từ mảng diseases
        diseases: (diseases || []).reduce((acc, item) => {
            acc[normalizeDiseaseKey(item.name)] = undefined;
            return acc;
        }, {}),
    }), [customer, diseases]);

    const getToothDataByType = (source) => {
        const result = {
            adult: [],
            children: [],
        };

        const teeth = source?.teeth || [];

        if (!Array.isArray(teeth)) return result;

        teeth.forEach(item => {
            const type = item.tooth_type || source?.tooth_type || 'adult';

            if (type === 'children') {
                result.children.push(item);
            } else {
                result.adult.push(item);
            }
        });

        return result;
    };

    const onFinish = useCallback(
        async (values) => {
            if (!customer?.id) return;
            //console.log("values", values);
            setIsLoading(true);
            try {
                const formatted = {
                    ...values,
                    tooths: {
                        tooth_type: toothType,
                        data: [
                            ...(toothDataByType.adult || []).map(item => ({
                                ...item,
                                tooth_type: "adult",
                            })),
                            ...(toothDataByType.children || []).map(item => ({
                                ...item,
                                tooth_type: "children",
                            })),
                        ],
                    },
                };

                console.log("formatted", formatted);
                if (record && record.id) {
                    await dispatch(updateGeneralExam(customerId, record.id, formatted));
                    await dispatch(getSpecificGeneralExam(record.id));
                } else {
                    await dispatch(createGeneralExam(customerId, formatted));
                    await dispatch(getGeneralExamsByCustomer({}, customerId));

                    // Đóng form
                    handleClose();
                }
                await dispatch(getSpecificCustomer(customerId));
            } finally {
                setIsLoading(false);
            }
        },
        [dispatch, customer?.id, customerId, toothDataByType, toothType, record?.id]
    );

    const onFinishFailed = (err) => {
        message.error('Vui lòng điền đầy đủ các trường bắt buộc');
    };

    const handleBirthdayChange = (e) => {
        const value = e.target.value;
        const date = moment(value, "DD/MM/YYYY", true);

        if (date.isValid()) {
            const age = moment().diff(date, "years");
            form.setFieldsValue({ age });
        } else {
            form.setFieldsValue({ age: "" });
        }
    };

    const handleToothDataChange = useCallback((data) => {
        setToothDataByType(prev => ({
            ...prev,
            [toothType]: Array.isArray(data) ? data : [],
        }));
    }, [toothType]);

    const onChangeToothTab = (value) => {
        setToothType(value);

        history.replace({
            pathname: location.pathname,
            search: qs.stringify({
                ...query,
                tooth_type: value,
            }),
        });
    };

    useEffect(() => {
        const urlToothType = query.tooth_type;

        if (
            visible &&
            (urlToothType === "adult" || urlToothType === "children") &&
            urlToothType !== toothType
        ) {
            setToothType(urlToothType);
        }
    }, [visible, query.tooth_type, toothType]);

    /**
     * Xử lý toggle chọn tất cả "Có" hoặc "Không"
     * @param {"yes"|"no"} value 
     */
    const handleToggleAll = (value) => {
        const currentDiseases = form.getFieldValue("diseases") || {};
        const isAllSelected = Object.values(currentDiseases).every(v => v === value);
        let newValues = {};

        // Bỏ chọn tất cả
        if (value === undefined) {
            newValues = (diseases || []).reduce((acc, item) => {
                acc[normalizeDiseaseKey(item.name)] = undefined;
                return acc;
            }, {});
            setActiveToggle(null);
            form.setFieldsValue({ diseases: newValues });
            return;
        }

        // Nếu tất cả đã là "Có" hoặc "Không" → toggle off (reset)
        if (isAllSelected && activeToggle === value) {
            newValues = (diseases || []).reduce((acc, item) => {
                acc[normalizeDiseaseKey(item.name)] = undefined;
                return acc;
            }, {});
            setActiveToggle(null);
        } else {
            // Chưa chọn hết → set toàn bộ về giá trị tương ứng
            newValues = (diseases || []).reduce((acc, item) => {
                acc[normalizeDiseaseKey(item.name)] = value;
                return acc;
            }, {});
            setActiveToggle(value);
        }

        form.setFieldsValue({ diseases: newValues });
    };

    useEffect(() => {
        if (visible) {
            setIsCreateMode(!record?.id);
        }
    }, [visible, record]);

    useEffect(() => {
        if (!visible) return;

        // CREATE MODE
        if (isCreateMode) {
            form.resetFields();
            form.setFieldsValue(defaultValues);

            setToothDataByType({
                adult: [],
                children: [],
            });

            setToothType(query.tooth_type || "adult");
            setIsApproved(false);
            return;
        }

        // UPDATE MODE
        const source = currentExam?.id ? currentExam : record;

        if (source && source.id) {
            const merged = {
                ...defaultValues,
                ...source,
                diseases: {
                    ...defaultValues.diseases,
                    ...(source.diseases || {}),
                },
            };

            form.resetFields();
            form.setFieldsValue(merged);

            setToothDataByType(getToothDataByType(source));
            setToothType(prev => {
                if (prev === "adult" || prev === "children") return prev;

                if (query.tooth_type === "adult" || query.tooth_type === "children") {
                    return query.tooth_type;
                }

                return source.tooth_type || "adult";
            });
            setIsApproved(!!source.is_approved);
        }
    }, [visible, isCreateMode, currentExam, record, defaultValues]);


    const reasons = [
        { value: "chinh_nha", label: "Chỉnh nha" },
        { value: "implant", label: "Implant" },
        { value: "tham_my_rang", label: "Thẩm mỹ răng" },
        { value: "kham_va_vsrm", label: "Khám và VSRM tổng quát" },
        { value: "dau_rang", label: "Đau răng" },
        { value: "nho_rang", label: "Nhổ răng" }
    ];

    const tinh_trang_rang = [
        { value: "level_1", label: "Level 1" },
        { value: "level_2", label: "Level 2" },
        { value: "level_3", label: "Level 3" }
    ];

    const mau_sac_rang = [
        { value: "nhiem_tetra", label: "Nhiễm tetra" },
        { value: "nhiem_flour", label: "Nhiễm flour" },
        { value: "mau_trang", label: "Bình thường" }
    ];

    const loadWards = async (province_code, reset = null) => {
        if (province_code) {
            try {
                const res = await dispatch(getListWards({ province_code })); // Gọi action
                if (reset) {
                    form.setFieldsValue({ wards_code: undefined });
                }
                setWards(res.data);
            } catch (error) {
                console.error("Lỗi khi tải danh sách phường/xã:", error);
            }
        }
    };

    const countryOptions = useMemo(() => {
        return config?.countries
            ? Object.entries(config.countries).map(([code, name]) => ({
                value: code,
                label: name,
            }))
            : [];
    }, [config?.countries]);

    const removeToothTypeFromUrl = () => {
        const nextQuery = { ...query };
        delete nextQuery.tooth_type;

        const nextSearch = qs.stringify(nextQuery);

        history.replace({
            pathname: location.pathname,
            search: nextSearch ? `?${nextSearch}` : '',
        });
    };

    const handleClose = () => {
        setIsCreateMode(false);
        form.resetFields();

        setToothDataByType({
            adult: [],
            children: [],
        });

        setToothType("adult");
        setIsApproved(false);
        removeToothTypeFromUrl();
        onCancel();
    };

    return (
        <div>
            <Drawer
                visible={visible}
                title={record ? "Cập nhật phiếu khám tổng quát" : "Thêm phiếu khám tổng quát"}
                onClose={handleClose}
                closable
                maskClosable={false}
                width={isMobile ? '100%' : '85%'}
                forceRender
                footer={
                    <div style={{ textAlign: "right" }}>
                        <Space>
                            <Button
                                htmlType="button"
                                size="large"
                                onClick={() => form.resetFields()}
                                icon={<ReloadOutlined />}
                            >
                                Reset
                            </Button>
                            <Button icon={<SaveOutlined />} size='large' loading={isLoading} type="primary" onClick={() => form.submit()}>
                                Lưu
                            </Button>
                        </Space>
                    </div>
                }
            >
                {visible && (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        disabled={isApproved}
                        initialValues={undefined}
                        className="form-readonly-custom-bg vertical-custom-item"
                    >
                        <Row gutter={8} className="mt-3 mb-3">
                            <Col span={6}>
                                <Form.Item
                                    label="Tên khách hàng"
                                    name="customer_name"
                                    rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Mã khách hàng"
                                    name="customer_code"
                                    rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                >
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Số căn cước"
                                    name="identity_number"
                                    rules={[{ required: false, message: "Bắt buộc chọn" }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Row gutter={8}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Ngày sinh"
                                            name="birthday"
                                            rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                        >
                                            <InputMask
                                                mask="99/99/9999"
                                                placeholder="dd/mm/yyyy"
                                                onChange={e => {
                                                    handleBirthdayChange(e);
                                                }}
                                            >
                                                {(inputProps) => <Input {...inputProps} />}
                                            </InputMask>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Tuổi"
                                            name="age"
                                            rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Giới tính"
                                    name="gender"
                                    rules={[{ required: true, message: "Bắt buộc chọn" }]}
                                >
                                    <BaseRadioList
                                        options={customerGender || []}
                                        name="gender"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Nghề nghiệp"
                                    name="occupation"
                                    rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Chọn ngành nghề"
                                        optionFilterProp="label"
                                        style={{ width: '100%' }}
                                        allowClear
                                    >
                                        {occupationOptions.map((group) => (
                                            <OptGroup key={group.group} label={group.group}>
                                                {group.options.map((item) => (
                                                    <Option key={item.value} value={item.value} label={item.label}>
                                                        {item.label}
                                                    </Option>
                                                ))}
                                            </OptGroup>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Dân tộc"
                                    name="ethnicity"
                                    rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                >
                                    <BaseSelect
                                        options={ethnicOptions || []}
                                        optionValue="value"
                                        optionLabel="label"
                                        showSearch
                                        placeholder="Chọn một bản ghi"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={6}>
                                <Form.Item
                                    label="Quốc tịch"
                                    name="nationality"
                                    rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                >
                                    <BaseSelect
                                        options={countryOptions || []}
                                        optionValue="value"
                                        optionLabel="label"
                                        showSearch
                                        placeholder="Chọn một bản ghi"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Tỉnh, thành phố"
                                    name="province_code"
                                    rules={[{ required: false, message: "Bắt buộc chọn" }]}
                                >
                                    <BaseSelect
                                        options={provinces || []}
                                        optionValue="code"
                                        optionLabel="name"
                                        showSearch
                                        placeholder="Chọn một bản ghi"
                                        style={{ width: '100%' }}
                                        onChange={(code) => loadWards(code, true)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Xã, phường"
                                    name="wards_code"
                                    rules={[{ required: false, message: "Bắt buộc chọn" }]}
                                >
                                    <BaseSelect
                                        options={wards || []}
                                        optionValue="code"
                                        optionLabel="name"
                                        showSearch
                                        placeholder="Chọn một bản ghi"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Thôn, phố"
                                    name="hamlet"
                                    rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Địa chỉ"
                                    name="address"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    label="Đối tượng"
                                    name="customer_type"
                                    rules={[{ required: false, message: "Bắt buộc chọn" }]}
                                >
                                    <BaseRadioList
                                        options={customerTypes || []}
                                        optionValue="value"
                                        optionLabel="label"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Số thẻ BHYT"
                                    name="bhyt_number"
                                    rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={18}>
                                <Form.Item
                                    label="Họ và tên thân nhân của NB khi cần báo tin"
                                    name="relative_info"
                                    rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    label="Điện thoại"
                                    name="relative_info_phone"
                                    rules={[
                                        { required: false, message: "Bắt buộc nhập" }
                                    ]}
                                >
                                    <Input placeholder="Nhập số điện thoại" />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Ghi chú"
                                    name="note"
                                    rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                                >
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Bác sĩ phụ trách"
                                    name="bac_si_phu_trach"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        options={doctors || []}
                                        optionValue="user_id"
                                        optionLabel="display_name"
                                        showSearch
                                        placeholder="Chọn bác sĩ"
                                        className="w-100"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={16}>
                                <Form.Item
                                    label="Nhân viên tư vấn"
                                    name="nhan_vien_tu_van_ids"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseSelect
                                        options={consultants || []}
                                        optionValue="user_id"
                                        optionLabel="display_name"
                                        showSearch
                                        mode="multiple"
                                        placeholder="Chọn một hoặc nhiều nhân viên tư vấn"
                                        className="w-100"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-0'>I. LÝ DO ĐẾN KHÁM</Divider>
                        <Row gutter={8}>
                            <Col xs={24} md={24} className="mt-2 mb-2">
                                <Form.Item
                                    label="&nbsp;"
                                    noStyle
                                    name="ly_do_den_kham"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseCheckBoxList
                                        options={reasons}
                                        optionValue="label"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Lý do khác"
                                    name="ly_do_khac"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <Input.TextArea rows={3} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item
                                    label="Mong muốn của khách hàng"
                                    name="mong_muon_khach_hang"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <Input.TextArea rows={3} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Card
                                    title="Bệnh lý cần lưu ý"
                                    size="small"
                                    extra={
                                        <Space>
                                            <Button
                                                type={activeToggle === "yes" ? "primary" : "default"}
                                                onClick={() => handleToggleAll("yes")}
                                            >
                                                Chọn tất cả Có
                                            </Button>
                                            <Button
                                                type={activeToggle === "no" ? "primary" : "default"}
                                                danger={activeToggle === "no"}
                                                onClick={() => handleToggleAll("no")}
                                            >
                                                Chọn tất cả Không
                                            </Button>
                                            <Button onClick={() => handleToggleAll(undefined)}>Bỏ chọn tất cả</Button>
                                        </Space>
                                    }
                                >
                                    <p>
                                        (Nội dung này rất quan trọng để các bác sĩ đưa ra chỉ định điều trị và kê đơn thuốc)
                                    </p>
                                    <Row gutter={[32, 16]}>
                                        <Col xs={24} sm={12}>
                                            {diseases.slice(0, 11).map((item, index) => (
                                                <Row key={item.id} align="middle">
                                                    <Col flex="auto">
                                                        {index + 1}. {item.name}
                                                    </Col>
                                                    <Col>
                                                        <Form.Item name={["diseases", normalizeDiseaseKey(item.name)]} noStyle>
                                                            <Radio.Group>
                                                                <Radio value="yes">Có</Radio>
                                                                <Radio value="no">Không</Radio>
                                                            </Radio.Group>
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            ))}
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            {diseases.slice(11).map((item, index) => (
                                                <Row key={item.id} align="middle">
                                                    <Col flex="auto">
                                                        {index + 12}. {item.name}
                                                    </Col>
                                                    <Col>
                                                        <Form.Item name={["diseases", normalizeDiseaseKey(item.name)]} noStyle>
                                                            <Radio.Group>
                                                                <Radio value="yes">Có</Radio>
                                                                <Radio value="no">Không</Radio>
                                                            </Radio.Group>
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            ))}
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-3'>II. KHÁM TỔNG QUÁT RĂNG MIỆNG</Divider>
                        <Row gutter={8}>
                            <Col xs={24} md={24}>
                                <Tabs
                                    activeKey={toothType}
                                    type="card"
                                    onChange={onChangeToothTab}
                                    destroyInactiveTabPane={false} // Giữ lại dữ liệu của tab không hoạt động
                                >
                                    <TabPane tab="Trẻ em" key="children" disabled={isApproved}>
                                        <ToothChartChild
                                            data={toothDataByType.children}
                                            onDataChange={handleToothDataChange}
                                        />
                                    </TabPane>

                                    <TabPane tab="Người trưởng thành" key="adult" disabled={isApproved}>
                                        <ToothChartGeneral
                                            data={toothDataByType.adult}
                                            onDataChange={handleToothDataChange}
                                        />
                                    </TabPane>
                                </Tabs>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Tình trạng cao răng"
                                    name="tinh_trang_cao_rang"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseRadioList
                                        options={tinh_trang_rang}
                                        optionValue="label"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Tình trạng nướu (lợi)"
                                    name="tinh_trang_nuou"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Màu sắc răng"
                                    name="mau_sac_rang"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <BaseRadioList
                                        optionValue="label"
                                        options={mau_sac_rang}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    label="Tình trạng răng khôn"
                                    name="tinh_trang_rang_khon"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8} className="mt-2">
                            <Col span={24}>
                                <Text strong>Tình trạng khớp cắn:</Text>
                            </Col>

                            {/* 1. Đau khớp */}
                            <Col span={24}>
                                <Row align="middle">
                                    <Col flex="auto">
                                        <Text>Khớp cắn / khớp thái dương có triệu chứng đau?</Text>
                                    </Col>
                                    <Col>
                                        <Form.Item name="pain" noStyle>
                                            <Radio.Group>
                                                <Radio value="yes">CÓ</Radio>
                                                <Radio value="no">KHÔNG</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>

                            {/* 2. Phát tiếng kêu */}
                            <Col span={24}>
                                <Row align="middle">
                                    <Col flex="auto">
                                        <Text>
                                            Khớp cắn / khớp thái dương có phát tiếng kêu khi há / đóng miệng tối đa?
                                        </Text>
                                    </Col>
                                    <Col>
                                        <Form.Item name="sound" noStyle>
                                            <Radio.Group>
                                                <Radio value="yes">CÓ</Radio>
                                                <Radio value="no">KHÔNG</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>

                            {/* 3. Đường há zích-zắc */}
                            <Col span={24}>
                                <Row align="middle">
                                    <Col flex="auto">
                                        <Text>Đường há ngậm miệng có dạng zích-zắc?</Text>
                                    </Col>
                                    <Col>
                                        <Form.Item name="zigzag" noStyle>
                                            <Radio.Group>
                                                <Radio value="yes">CÓ</Radio>
                                                <Radio value="no">KHÔNG</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-2'>III. CHẨN ĐOÁN TRÊN PHIM X-QUANG</Divider>
                        <Row gutter={8}>
                            <Col span={24} className="mb-2">
                                <Image.PreviewGroup>
                                    <Space wrap>
                                        {(Array.isArray(customer?.x_quang_images) ? customer.x_quang_images : []).map((item, index) => (
                                            <Image
                                                key={index}
                                                height={80}
                                                width={100}
                                                src={item?.file_url_thumb || 'https://placehold.co/200x200?text=No+Image'}
                                                preview={{
                                                    src: item.file_url_original,  // khi phóng to, mở ảnh gốc
                                                }}
                                                alt={`X-quang ${index + 1}`}
                                                fallback="https://placehold.co/200x200?text=No+Image"
                                                style={{
                                                    width: "100%",   // không vượt quá khung
                                                    height: "100%",  // luôn giữ tỷ lệ
                                                    objectFit: "cover", // luôn hiển thị toàn bộ ảnh
                                                    objectPosition: "center center",
                                                }}
                                            />
                                        ))}
                                    </Space>

                                    {/* Trường hợp không có ảnh */}
                                    {(!Array.isArray(customer?.x_quang_images) || customer.x_quang_images.length === 0) && (
                                        <div style={{ textAlign: 'center', width: '100%', color: '#999', padding: '12px 0' }}>
                                            Không có hình ảnh X-quang
                                        </div>
                                    )}
                                </Image.PreviewGroup>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label="Chẩn đoán trên phim x-quang"
                                    name="chan_doan_tren_phim_x_quang"
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <Input.TextArea rows={4} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-2'>IV. CHẨN ĐOÁN SƠ BỘ</Divider>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item
                                    name="chan_doan_so_bo"
                                    noStyle
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <Input.TextArea rows={4} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-2'>V. HƯỚNG ĐIỀU TRỊ</Divider>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item
                                    label="Lấy cao răng, điều trị viêm lợi"
                                    name="lay_cao_rang"
                                    rules={[{ required: false }]}
                                >
                                    <Input.TextArea rows={2} placeholder="Nhập chi tiết nếu có..." />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    label="Trám các răng"
                                    name="tram_cac_rang"
                                    rules={[{ required: false }]}
                                >
                                    <Input.TextArea rows={2} placeholder="Nhập chi tiết nếu có..." />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label="Điều trị tủy các răng"
                                    name="dieu_tri_tuy_cac_rang"
                                    rules={[{ required: false }]}
                                >
                                    <Input.TextArea rows={2} placeholder="Nhập chi tiết nếu có..." />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label="Trồng các răng"
                                    name="trong_cac_rang"
                                    rules={[{ required: false }]}
                                >
                                    <Input.TextArea rows={2} placeholder="Nhập chi tiết nếu có..." />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label="Làm cầu, chụp"
                                    name="lam_cau_chup"
                                    rules={[{ required: false }]}
                                >
                                    <Input.TextArea rows={2} placeholder="Nhập chi tiết nếu có..." />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item label="Nhổ răng" name="nho_rang" rules={[{ required: false }]}>
                                    <Input.TextArea rows={2} placeholder="Nhập chi tiết nếu có..." />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label="Chăm sóc và điều trị khác"
                                    name="dieu_tri_khac"
                                    rules={[{ required: false }]}
                                >
                                    <Checkbox.Group style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                                        <Checkbox value="Lấy cao răng">Lấy cao răng</Checkbox>
                                        <Checkbox value="Điều trị viêm lợi">Điều trị viêm lợi</Checkbox>
                                        <Checkbox value="Tẩy trắng răng">Tẩy trắng răng</Checkbox>
                                    </Checkbox.Group>
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label="Chỉ định khác"
                                    name="chi_dinh_khac"
                                    rules={[{ required: false }]}
                                >
                                    <Input.TextArea rows={3} placeholder="Nhập chỉ định khác (nếu có)" />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label="Chi tiết chỉ định"
                                    name="chi_tiet_chi_dinh"
                                    rules={[{ required: false }]}
                                >
                                    <Input.TextArea rows={5} placeholder="Ghi rõ nội dung chỉ định cụ thể..." />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider orientation="left" orientationMargin="0" className='mt-3 mb-2'>VI. NỘI DUNG TƯ VẤN <span style={{ color: "#666", fontStyle: 'italic', fontWeight: 'normal' }}>(dành cho nhân viên tư vấn)</span></Divider>
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item
                                    name="noi_dung_tu_van"
                                    noStyle
                                    rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                >
                                    <Input.TextArea rows={6} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Drawer>
        </div >
    );
};

export default GeneralExamForm;
